/* eslint-disable react/no-unescaped-entities */
import { Paper, Box, Chip, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import type { TemplateTestResultItem } from "../types/api-types";
import { getResultCodeLabel, getResultCodeColor } from "./result-codes";

interface TestResultCardProps {
  result: TemplateTestResultItem;
  index: number;
}

const TestResultCard = ({ result, index }: TestResultCardProps) => {
  const resultColor = getResultCodeColor(result.resultCode);
  const resultLabel = getResultCodeLabel(result.resultCode);

  const getResultIcon = () => {
    switch (resultColor) {
      case "success":
        return <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />;
      case "warning":
        return <WarningIcon sx={{ fontSize: 16, color: "warning.main" }} />;
      case "error":
        return <ErrorIcon sx={{ fontSize: 16, color: "error.main" }} />;
      default:
        return <InfoIcon sx={{ fontSize: 16, color: "info.main" }} />;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        bgcolor: `${resultColor}.50`,
        border: 1,
        borderColor: `${resultColor}.200`,
        borderRadius: 1.5,
        borderLeft: 3,
        borderLeftColor: `${resultColor}.main`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "text.secondary",
            minWidth: 20,
          }}
        >
          #{index + 1}
        </Typography>
        {getResultIcon()}
        <Chip
          label={resultLabel}
          size="small"
          color={resultColor}
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 500,
            "& .MuiChip-label": { px: 1 },
          }}
        />
      </Box>

      {/* Values - Compact Table Layout */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              color: "text.secondary",
              minWidth: 40,
              pt: 0.5,
            }}
          >
            Input:
          </Typography>
          <Box
            sx={{
              flex: 1,
              px: 1,
              py: 0.5,
              bgcolor: "white",
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "0.7rem",
              wordBreak: "break-word",
              border: 1,
              borderColor: "grey.200",
            }}
          >
            {result.originalValue}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              color: "text.secondary",
              minWidth: 40,
              pt: 0.5,
            }}
          >
            Output:
          </Typography>
          <Box
            sx={{
              flex: 1,
              px: 1,
              py: 0.5,
              bgcolor: "white",
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "0.7rem",
              wordBreak: "break-word",
              border: 1,
              borderColor: `${resultColor}.300`,
            }}
          >
            {result.newValue || "—"}
          </Box>
        </Box>
      </Box>

      {/* Additional Info */}
      {(result.possibleReClassificationTemplateId > 0 ||
        (result.extendedResults && result.extendedResults.length > 0)) && (
        <Box
          sx={{
            mt: 0.75,
            pt: 0.75,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Stack spacing={0.25}>
            {result.possibleReClassificationTemplateId > 0 && (
              <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                Suggested: Template {result.possibleReClassificationTemplateId}
              </Typography>
            )}
            {result.extendedResults && result.extendedResults.length > 0 && (
              <>
                {result.extendedResults.map((ext) => (
                  <Typography
                    key={`${result.id}-${ext.description}`}
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      fontFamily: "monospace",
                      color: "text.secondary",
                    }}
                  >
                    {ext.description}: {JSON.stringify(ext.value)}
                  </Typography>
                ))}
              </>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default TestResultCard;
